import { useState, useRef, useEffect, useCallback } from 'react';
import { anprAPI } from '../services/api';
import { toast } from 'sonner';

function playAlert(decision) {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    if (decision === 'GREEN') {
      oscillator.frequency.value = 880; oscillator.type = 'sine'; gainNode.gain.value = 0.3;
      oscillator.start(); setTimeout(() => oscillator.stop(), 200);
    } else if (decision === 'RED') {
      oscillator.frequency.value = 440; oscillator.type = 'square'; gainNode.gain.value = 0.2;
      oscillator.start(); setTimeout(() => { oscillator.frequency.value = 330; setTimeout(() => oscillator.stop(), 200); }, 200);
    } else {
      oscillator.frequency.value = 660; oscillator.type = 'triangle'; gainNode.gain.value = 0.2;
      oscillator.start(); setTimeout(() => oscillator.stop(), 150);
    }
  } catch (e) { console.error('Error playing sound:', e); }
}

/**
 * Custom hook for ANPR live feed — WebSocket + polling.
 *
 * Dependency note: `anprAPI`, `toast`, and `playAlert` are module-level
 * constants that never change.  They are omitted from useCallback deps
 * intentionally because including them would not change behaviour but
 * would add noise.  The `soundEnabledRef` pattern avoids stale closures.
 */
export function useANPR() {
  const [anprConnected, setAnprConnected] = useState(false);
  const [anprConfigured, setAnprConfigured] = useState(false);
  const [latestReading, setLatestReading] = useState(null);
  const [recentReadings, setRecentReadings] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showSnapshot, setShowSnapshot] = useState(true);
  const wsRef = useRef(null);
  const lastReadingIdRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const soundEnabledRef = useRef(soundEnabled);

  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);

  const handleNewReading = useCallback((reading, source) => {
    setLatestReading(reading);
    setRecentReadings(prev => {
      if (prev.some(r => r.id === reading.id)) return prev;
      return [reading, ...prev.slice(0, 9)];
    });
    if (soundEnabledRef.current && source !== 'initial') playAlert(reading.decision);
    if (source !== 'initial') {
      const toastType = reading.decision === 'GREEN' ? toast.success : reading.decision === 'RED' ? toast.error : toast.warning;
      toastType(`Vehicle Detected: ${reading.plate || 'No Plate'} - ${reading.decision}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = useCallback(() => {
    let pollCount = 0;
    pollingIntervalRef.current = setInterval(async () => {
      try {
        pollCount++;
        if (pollCount % 10 === 0) {
          const fullRes = await anprAPI.getLatest(5);
          if (fullRes.data.readings?.length > 0) {
            setRecentReadings(fullRes.data.readings);
            setLatestReading(fullRes.data.readings[0]);
            lastReadingIdRef.current = fullRes.data.readings[0].id;
          }
          return;
        }
        const latestRes = await anprAPI.getLatest(1);
        if (latestRes.data.readings?.length > 0) {
          const newReading = latestRes.data.readings[0];
          if (newReading.id !== lastReadingIdRef.current) {
            lastReadingIdRef.current = newReading.id;
            handleNewReading(newReading, 'poll');
          }
        }
      } catch (error) { console.error('Polling error:', error); }
    }, 3000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleNewReading]);

  const connectWebSocket = useCallback(() => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let wsUrl;
      if (backendUrl) {
        const baseUrl = backendUrl.replace(/\/api$/, '').replace(/\/$/, '');
        const wsProtocol = baseUrl.startsWith('https') ? 'wss:' : 'ws:';
        const host = baseUrl.replace(/^https?:\/\//, '');
        wsUrl = `${wsProtocol}//${host}/ws/anpr`;
      } else {
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${wsProtocol}//${window.location.host}/ws/anpr`;
      }
      wsRef.current = new WebSocket(wsUrl);
      wsRef.current.onopen = () => { setAnprConnected(true); };
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'anpr_reading' || message.type === 'initial') {
            handleNewReading(message.data, message.type);
          }
        } catch (e) { console.error('Error parsing WebSocket message:', e); }
      };
      wsRef.current.onclose = () => { setAnprConnected(false); };
      wsRef.current.onerror = () => { setAnprConnected(false); };
      const heartbeatInterval = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send('ping');
      }, 25000);
      return () => clearInterval(heartbeatInterval);
    } catch (e) {
      console.error('WebSocket connection failed:', e);
      setAnprConnected(false);
    }
  }, [handleNewReading]);

  useEffect(() => {
    const checkAnprStatus = async () => {
      try {
        const response = await anprAPI.getStatus();
        setAnprConfigured(response.data.configured);
        if (response.data.configured) {
          const latestRes = await anprAPI.getLatest(5);
          if (latestRes.data.readings?.length > 0) {
            setRecentReadings(latestRes.data.readings);
            setLatestReading(latestRes.data.readings[0]);
            lastReadingIdRef.current = latestRes.data.readings[0].id;
          }
          connectWebSocket();
          startPolling();
        }
      } catch (error) { console.error('Error checking ANPR status:', error); }
    };
    checkAnprStatus();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, [connectWebSocket, startPolling]);

  return {
    anprConnected, anprConfigured,
    latestReading, setLatestReading,
    recentReadings,
    soundEnabled, setSoundEnabled,
    showSnapshot, setShowSnapshot,
    playAlert,
  };
}

export { playAlert };
